import React, { createContext, useState, useContext, useEffect } from 'react';
import { corretoresIniciais, metaAnual, anos } from '../data/mockData';
import { DEFAULT_THEME_KEY, getTheme } from '../config/themes';
import { updateColors } from '../config/colors';
import { isCorretorSemPontuacao } from '../utils/corretorUtils';
import {
  getCorretores,
  getAllVendas,
  createCorretor as createCorretorService,
  updateCorretor,
  deleteCorretor as deleteCorretorService,
  toggleCorretorAtivo as toggleCorretorAtivoService,
  upsertVenda,
  getConfig,
  getConfigs,
  setConfig as setConfigService,
  transformCorretoresWithVendas,
} from '../services/supabaseService';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp deve ser usado dentro de AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Load theme from localStorage or use default
  const [selectedThemeKey, setSelectedThemeKey] = useState(() => {
    const savedTheme = localStorage.getItem('selectedTheme');
    return savedTheme || DEFAULT_THEME_KEY;
  });

  const [corretores, setCorretores] = useState([]);
  const [metasPorAno, setMetasPorAno] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Save theme to localStorage and update colors when it changes
  useEffect(() => {
    localStorage.setItem('selectedTheme', selectedThemeKey);
    // Update colors object for components that import it directly
    updateColors(selectedThemeKey);
  }, [selectedThemeKey]);

  // Function to load data from Supabase (can be called silently)
  // Flag para prevenir múltiplas chamadas simultâneas
  const loadingRef = React.useRef(false);
  
  const loadData = React.useCallback(async (silent = false) => {
    // Prevenir múltiplas chamadas simultâneas
    if (loadingRef.current) {
      return;
    }
    
    loadingRef.current = true;
    let isMounted = true; // Flag to prevent state updates if component unmounts
    
    const loadDataInternal = async () => {
      try {
        // Only show loading on initial load or manual refresh (not on silent auto-refresh)
        if (!silent) {
          setLoading(true);
        }
        setError(null);

        // Check if Supabase is configured
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        if (!supabaseUrl) {
          const errorMsg = 'Supabase não está configurado. Por favor, configure as variáveis de ambiente REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY';
          console.error(errorMsg);
          if (isMounted) {
            setError(errorMsg);
            // Don't reset corretores if they already exist
            // setCorretores([]);
            // Set default metas for all years (0 if not configured)
            const defaultMetas = {};
            anos.forEach(ano => {
              defaultMetas[ano] = 0;
            });
            setMetasPorAno(defaultMetas);
            setLoading(false);
          }
          return;
        }

        // Load corretores first, then vendas (to avoid timeout)
        const corretoresData = await getCorretores();
        const vendasData = await getAllVendas();

        // Only update state if component is still mounted
        if (!isMounted) {
          console.log('Component unmounted, skipping state update');
          return;
        }

        // Transform data to app format
        const corretoresWithVendas = transformCorretoresWithVendas(
          corretoresData,
          vendasData
        );

        console.log('Corretores after transformation:', corretoresWithVendas?.length || 0);
        setCorretores(corretoresWithVendas);

        // Load metas for all years directly from Supabase (source of truth)
        const metas = {};
        
        // Cache fallback preparation
        let metasFromCache = {};
        try {
          anos.forEach(ano => {
            const cached = localStorage.getItem(`meta_${ano}`);
            if (cached) {
              metasFromCache[ano] = parseFloat(cached);
            }
          });
        } catch (err) {
          console.warn('Error reading metas from cache:', err);
        }
        
        // Query Supabase for all year metas
        try {
          const chaves = anos.map(ano => `meta_${ano}`);
          const configs = await getConfigs(chaves);
          
          anos.forEach(ano => {
            const chave = `meta_${ano}`;
            if (configs[chave] !== undefined && configs[chave] !== null) {
              const numMeta = parseFloat(configs[chave]);
              metas[ano] = isNaN(numMeta) ? 0 : numMeta;
              try {
                localStorage.setItem(`meta_${ano}`, configs[chave]);
              } catch (err) {}
            } else {
              metas[ano] = metasFromCache[ano] || 0;
            }
          });
        } catch (dbErr) {
          console.warn('Erro ao buscar metas no Supabase, usando cache:', dbErr);
          anos.forEach(ano => {
            metas[ano] = metasFromCache[ano] || 0;
          });
        }
        setMetasPorAno(metas);

        // Only update loading state if not silent
        if (!silent) {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading data from Supabase:', err);
        if (isMounted) {
          // Only show errors on non-silent loads (initial load or manual refresh)
          if (!silent) {
            // Check if it's a CORS or network error
            const isCorsError = err.message?.includes('CORS') || 
                               err.message?.includes('Failed to fetch') ||
                               err.message?.includes('NetworkError') ||
                               err.message?.includes('Erro de conexão') ||
                               err.name === 'TypeError';
            
            // Check if it's a timeout error
            const isTimeoutError = err.message?.includes('timeout') || 
                                   err.message?.includes('canceling statement') ||
                                   err.code === 'PGRST301';
            
            // Check if it's an authentication error
            const isAuthError = err.message?.includes('Invalid API key') || 
                               err.message?.includes('401') ||
                               err.message?.includes('Unauthorized');
            
            if (isCorsError) {
              const errorMsg = 'Erro de conexão com o banco de dados (CORS). Verifique:\n\n1. Se as variáveis REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY estão corretas\n2. Se as políticas RLS no Supabase permitem acesso anônimo\n3. Se o projeto Supabase está ativo\n4. Se há problemas de rede/firewall';
              setError(errorMsg);
            } else if (isAuthError) {
              const errorMsg = `Erro de autenticação: ${err.message || 'Chave da API inválida'}. Verifique se as variáveis de ambiente REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY estão configuradas corretamente no Easypanel e se um novo build foi feito após configurá-las.`;
              setError(errorMsg);
            } else if (isTimeoutError) {
              setError('Timeout ao carregar dados. A query está demorando muito. Tente recarregar a página ou verifique a conexão com o banco de dados.');
            } else {
              setError(err.message || 'Erro ao carregar dados do banco de dados');
            }
          }
          
          // Don't reset corretores on error - keep existing data
          // setCorretores([]);
          // Set default metas for all years (0 if not configured)
          const defaultMetas = {};
          anos.forEach(ano => {
            defaultMetas[ano] = 0;
          });
          setMetasPorAno(defaultMetas);
          
          // Only update loading state if not silent
          if (!silent) {
            setLoading(false);
          }
        }
      }
    };

    try {
      await loadDataInternal();
    } finally {
      loadingRef.current = false;
    }
    
    return () => {
      isMounted = false;
      loadingRef.current = false;
    };
  }, []);

  // Load data from Supabase on mount (initial load with loading indicator)
  useEffect(() => {
    loadData(false); // Show loading on initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Auto-refresh data every 30 minutes (otimizado para reduzir egress do Supabase)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loadingRef.current) {
        loadData(true); // Silent refresh (no loading indicator)
      }
    }, 1800000); // 30 minutes (1800000ms) - otimizado para reduzir consumo de egress

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only set up interval once

  // Refresh data when window gains focus (user returns to tab) - silently
  // Com debounce aumentado para evitar múltiplas chamadas e reduzir egress
  useEffect(() => {
    let lastFocusTime = 0;
    const DEBOUNCE_MS = 600000; // Só refresh se passou mais de 10 minutos desde o último focus
    
    const handleFocus = () => {
      const now = Date.now();
      if (!loadingRef.current && (now - lastFocusTime) > DEBOUNCE_MS) {
        lastFocusTime = now;
        loadData(true); // Silent refresh
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only set up listener once

  // Get current theme colors
  const currentTheme = getTheme(selectedThemeKey);

  const changeTheme = (themeKey) => {
    setSelectedThemeKey(themeKey);
  };

  const calcularVGVTotal = (ano) => {
    return corretores
      .filter(c => c.ativo || isCorretorSemPontuacao(c)) // Corretores ativos e vendas da Loja contabilizadas no acumulado
      .reduce((total, corretor) => {
        if (corretor.vendas && corretor.vendas[ano]) {
          const vendasAno = Object.values(corretor.vendas[ano]).reduce((sum, v) => {
            // Support both old format (number) and new format (object)
            const valor = typeof v === 'object' ? (v.valor || 0) : v;
            return sum + valor;
          }, 0);
          return total + vendasAno;
        }
        return total;
      }, 0);
  };

  const calcularVGVMes = (ano, mes) => {
    return corretores
      .filter(c => c.ativo || isCorretorSemPontuacao(c)) // Corretores ativos e vendas da Loja contabilizadas no acumulado
      .reduce((total, corretor) => {
        const venda = corretor.vendas?.[ano]?.[mes];
        if (venda) {
          // Support both old format (number) and new format (object)
          const valor = typeof venda === 'object' ? (venda.valor || 0) : venda;
          return total + valor;
        }
        return total;
      }, 0);
  };

  const atualizarVenda = async (
    corretorId,
    ano,
    mes,
    valor,
    agenciamento = 0,
    numeroContratos = 0,
    pontuacaoMeta = 0
  ) => {
    try {
      // Update in Supabase - required
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase não está configurado. Configure as variáveis de ambiente.');
      }

      await upsertVenda(corretorId, ano, mes, valor, agenciamento, numeroContratos, pontuacaoMeta);

      // Update local state
      setCorretores(prev => prev.map(corretor => {
        if (corretor.id === corretorId) {
          const novoCorretor = { ...corretor };
          if (!novoCorretor.vendas) novoCorretor.vendas = {};
          if (!novoCorretor.vendas[ano]) novoCorretor.vendas[ano] = {};
          novoCorretor.vendas[ano][mes] = {
            valor: valor,
            pontuacao_meta: pontuacaoMeta,
            agenciamento: agenciamento,
            numero_contratos: numeroContratos,
          };
          return novoCorretor;
        }
        return corretor;
      }));
    } catch (err) {
      console.error('Error updating venda:', err);
      throw err;
    }
  };

  const atualizarVendasBatch = async (vendasArray) => {
    try {
      // Update in Supabase - required
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase não está configurado. Configure as variáveis de ambiente.');
      }

      const supabaseService = await import('../services/supabaseService');
      await supabaseService.upsertVendasBatch(vendasArray);

      // Update local state for all vendas
      setCorretores(prev => prev.map(corretor => {
        const vendasDoCorretor = vendasArray.filter(v => v.corretorId === corretor.id);
        if (vendasDoCorretor.length === 0) return corretor;
        
        const novoCorretor = { ...corretor };
        if (!novoCorretor.vendas) novoCorretor.vendas = {};
        
        vendasDoCorretor.forEach(venda => {
          if (!novoCorretor.vendas[venda.ano]) novoCorretor.vendas[venda.ano] = {};
          novoCorretor.vendas[venda.ano][venda.mes] = {
            valor: venda.valor || 0,
            pontuacao_meta: venda.pontuacaoMeta || 0,
            agenciamento: venda.agenciamento || 0,
            numero_contratos: venda.numeroContratos || 0,
          };
        });
        
        return novoCorretor;
      }));
    } catch (err) {
      console.error('Error updating vendas batch:', err);
      throw err;
    }
  };

  const atualizarStatusAtivo = async (corretorId, ativo) => {
    try {
      // Update in Supabase - required
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase não está configurado. Configure as variáveis de ambiente.');
      }

      await toggleCorretorAtivoService(corretorId, ativo);

      // Update local state
      setCorretores(prev => prev.map(corretor => 
        corretor.id === corretorId ? { ...corretor, ativo } : corretor
      ));
    } catch (err) {
      console.error('Error updating corretor status:', err);
      throw err;
    }
  };

  const adicionarCorretor = async (novoCorretor) => {
    try {
      // Create in Supabase - required
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase não está configurado. Configure as variáveis de ambiente.');
      }

      const createdCorretor = await createCorretorService(novoCorretor);

      // Add to local state
      setCorretores(prev => [...prev, createdCorretor]);
      return createdCorretor;
    } catch (err) {
      console.error('Error adding corretor:', err);
      throw err;
    }
  };

  const atualizarCorretor = async (corretorId, updates) => {
    try {
      // Update in Supabase - required
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase não está configurado. Configure as variáveis de ambiente.');
      }

      await updateCorretor(corretorId, updates);

      // Update local state
      setCorretores(prev => prev.map(corretor => 
        corretor.id === corretorId ? { ...corretor, ...updates } : corretor
      ));
    } catch (err) {
      console.error('Error updating corretor:', err);
      throw err;
    }
  };

  const removerCorretor = async (corretorId) => {
    try {
      // Delete in Supabase - required
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase não está configurado. Configure as variáveis de ambiente.');
      }

      await deleteCorretorService(corretorId);

      // Update local state
      setCorretores(prev => prev.filter(corretor => corretor.id !== corretorId));
    } catch (err) {
      console.error('Error deleting corretor:', err);
      throw err;
    }
  };

  const salvarMeta = async (ano, novaMeta) => {
    try {
      // Save to Supabase - required
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      
      if (supabaseUrl) {
        try {
          await setConfigService(`meta_${ano}`, novaMeta);
          console.log(`salvarMeta: Meta ${ano} salva no Supabase com sucesso`);
        } catch (supabaseError) {
          console.warn(`salvarMeta: Erro ao salvar no Supabase, usando localStorage como fallback:`, supabaseError);
          // Fallback para localStorage já foi feito no setConfigService
        }
      } else {
        // Se Supabase não estiver configurado, salvar apenas no localStorage
        console.log(`salvarMeta: Supabase não configurado, salvando apenas no localStorage`);
        localStorage.setItem(`meta_${ano}`, novaMeta.toString());
      }

      // Sempre atualizar o estado local (mesmo se Supabase falhar)
      setMetasPorAno(prev => ({
        ...prev,
        [ano]: novaMeta
      }));
      
      // Também salvar no localStorage como backup adicional
      try {
        localStorage.setItem(`meta_${ano}`, novaMeta.toString());
      } catch (localError) {
        console.warn('Erro ao salvar meta no localStorage (não crítico):', localError);
      }
    } catch (err) {
      console.error('Error saving meta:', err);
      // Não lançar erro - sempre atualizar estado local mesmo se falhar
      setMetasPorAno(prev => ({
        ...prev,
        [ano]: novaMeta
      }));
      throw err;
    }
  };

  const getMetaPorAno = (ano) => {
    return metasPorAno[ano] || 0;
  };

  // Function to manually reload data (with loading indicator)
  const reloadData = React.useCallback(() => {
    loadData(false); // Show loading on manual refresh
  }, [loadData]);

  const value = {
    corretores,
    metasPorAno,
    getMetaPorAno,
    loading,
    error,
    calcularVGVTotal,
    calcularVGVMes,
    isCorretorSemPontuacao,
    atualizarVenda,
    atualizarVendasBatch,
    atualizarStatusAtivo,
    adicionarCorretor,
    atualizarCorretor,
    removerCorretor,
    salvarMeta,
    selectedThemeKey,
    currentTheme,
    changeTheme,
    loadData, // Expose for internal use
    reloadData, // Expose for manual refresh button
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

